import { supabase } from "./supabase";

// ─────────────────────────────────────────────────────────────────────────────
// Round 12 — Home attendance stats on server truth (R3).
//
// `fetchMyStats` reads `public.get_my_stats()` (SECURITY DEFINER, RLS-owner;
// parallel server task r12a). The Home tiles, belt rank, pending line and the
// tile drill-down lists all derive from this single response. The local
// `gameState` model is no longer read for any of these numbers (R4).
//
// Closed-token pattern of `lib/xpLedger.js`: a thrown transport failure or an
// RPC `error` is closed to a small `{ ok: false, code }` shape so the UI hides,
// never crashes, and keeps the last values within the same identity/generation.
// `season`/`allTime` aggregates are passed through as the server shapes them;
// only the top-level keys and the `games[]`/`players[]` entries are camelCased.
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_STATS = () => ({
  seasonYear: null,
  pendingGames: 0,
  season: { games: 0, stadiums: 0, players: 0, teams: 0 },
  allTime: { games: 0, stadiums: 0, players: 0, teams: 0 },
  games: [],
});

function mapPlayer(p) {
  return {
    playerId: p?.player_id ?? null,
    name: p?.name ?? null,
    side: p?.side ?? null,
    tries: p?.tries ?? 0,
    goals: p?.goals ?? 0,
    fieldGoals: p?.field_goals ?? 0,
    milestones: Array.isArray(p?.milestones) ? p.milestones : [],
  };
}

function mapGame(g) {
  return {
    fixtureId: g?.fixture_id ?? null,
    legacyId: g?.legacy_id ?? null,
    seasonYear: g?.season_year ?? null,
    round: g?.round ?? null,
    matchDate: g?.match_date ?? null,
    kickoffLocal: g?.kickoff_local ?? null,
    kickoffAt: g?.kickoff_at ?? null,
    homeRef: g?.home_ref ?? null,
    awayRef: g?.away_ref ?? null,
    homeTeamId: g?.home_team_id ?? null,
    awayTeamId: g?.away_team_id ?? null,
    homeScore: g?.home_score ?? null,
    awayScore: g?.away_score ?? null,
    venue: g?.venue ?? null,
    checkedInAt: g?.checked_in_at ?? null,
    players: Array.isArray(g?.players) ? g.players.map(mapPlayer) : [],
  };
}

/**
 * Fetch the signed-in user's Home attendance stats from `get_my_stats`.
 * Returns `{ ok: true, stats }` (camelCased) on success, or
 * `{ ok: false, code: 'network_error' | 'server_error' }` on any thrown
 * transport failure or RPC error (console.warn; never throws). Display-only.
 */
export async function fetchMyStats() {
  let data, error;
  try {
    ({ data, error } = await supabase.rpc("get_my_stats"));
  } catch (err) {
    console.warn("[stats] could not reach stats service:", err?.message || err);
    return { ok: false, code: "network_error" };
  }
  if (error) {
    console.warn("[stats] stats service error:", error?.message || error);
    return { ok: false, code: "server_error" };
  }
  const payload = data && typeof data === "object" ? data : {};
  const stats = {
    seasonYear: payload.season_year ?? null,
    pendingGames: Number(payload.pending_games || 0),
    season: payload.season ?? { games: 0, stadiums: 0, players: 0, teams: 0 },
    allTime: payload.all_time ?? { games: 0, stadiums: 0, players: 0, teams: 0 },
    games: Array.isArray(payload.games) ? payload.games.map(mapGame) : [],
  };
  return { ok: true, stats };
}

export const EMPTY_MY_STATS = EMPTY_STATS;
